import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Plus, ArrowLeft, Clock, CheckCheck, Check,
  AlertCircle, User, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const CustomerMessages = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [newMessageData, setNewMessageData] = useState({
    recipient_id: '',
    subject: '',
    content: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAnnouncements(),
        fetchConversations(),
        fetchContacts()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/messages`, getAuthHeader());
      // Filter announcements that are for customers or for everyone
      const filtered = response.data.filter(msg => {
        const targetRoles = msg.target_roles || [];
        // Show if no target roles (for everyone) or if customer is in target roles
        return targetRoles.length === 0 || targetRoles.includes('customer');
      });
      setAnnouncements(filtered);
    } catch (error) {
      console.error('Failed to fetch announcements');
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/customer/conversations`, getAuthHeader());
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/customer/messageable-contacts`, getAuthHeader());
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const openConversation = async (conv) => {
    setSelectedConversation(conv);
    try {
      const response = await axios.get(`${API}/direct-messages/conversations/${conv.id}`, getAuthHeader());
      // Backend returns { conversation, messages }
      setConversationMessages(response.data.messages || []);
      // Refresh conversations to update unread counts
      fetchConversations();
      // Dispatch event to update badge in Layout immediately (real-time update)
      window.dispatchEvent(new CustomEvent('conversationOpened'));
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSendNewMessage = async (e) => {
    e.preventDefault();
    if (!newMessageData.recipient_id || !newMessageData.subject || !newMessageData.content) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await axios.post(`${API}/customer/conversations`, newMessageData, getAuthHeader());
      toast.success('Message sent successfully');
      setNewMessageOpen(false);
      setNewMessageData({ recipient_id: '', subject: '', content: '' });
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedConversation) return;
    
    try {
      await axios.post(
        `${API}/direct-messages/conversations/${selectedConversation.id}/reply`,
        { content: replyText },
        getAuthHeader()
      );
      setReplyText('');
      // Refresh messages
      const response = await axios.get(`${API}/direct-messages/conversations/${selectedConversation.id}`, getAuthHeader());
      setConversationMessages(response.data.messages || []);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      timeZone: 'Africa/Lagos',
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-700',
      distributor: 'bg-purple-100 text-purple-700'
    };
    const labels = {
      admin: 'Admin',
      distributor: 'Distributor'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[role] || 'bg-slate-100 text-slate-700'}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      normal: 'bg-slate-100 text-slate-700',
      important: 'bg-amber-100 text-amber-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[priority] || styles.normal}`}>
        {priority?.toUpperCase()}
      </span>
    );
  };

  // If viewing a conversation
  if (selectedConversation) {
    const isInitiator = selectedConversation.initiated_by === user.id;
    const otherPartyName = isInitiator ? selectedConversation.recipient_name : selectedConversation.initiated_by_name;
    const otherPartyRole = isInitiator ? selectedConversation.recipient_role : selectedConversation.initiated_by_role;
    
    return (
      <div className="space-y-6" data-testid="customer-messages-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setSelectedConversation(null)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{selectedConversation.subject}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-slate-600">with {otherPartyName}</span>
              {getRoleBadge(otherPartyRole)}
            </div>
          </div>
        </div>

        <div className="card p-4 space-y-4 max-h-[500px] overflow-y-auto">
          {conversationMessages.map((msg) => {
            const isMine = msg.sender_id === user.id;
            return (
              <div 
                key={msg.id} 
                className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] rounded-lg p-3 ${
                  isMine ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-900'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <div className={`flex items-center gap-2 mt-2 text-xs ${
                    isMine ? 'text-emerald-200' : 'text-slate-500'
                  }`}>
                    <Clock className="w-3 h-3" />
                    {formatDate(msg.created_at)}
                    {isMine && (
                      msg.is_read ? (
                        <span className="flex items-center gap-1">
                          <CheckCheck className="w-3 h-3" /> Read
                        </span>
                      ) : (
                        <Check className="w-3 h-3" />
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={handleReply} className="flex gap-2">
          <Input
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            className="flex-1"
          />
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
            <Send className="w-4 h-4 mr-2" /> Send
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-messages-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Messages
        </h1>
        <p className="text-lg text-slate-600 mt-2">
          View announcements and communicate with the farm
        </p>
      </div>

      <Tabs defaultValue="announcements" className="space-y-4">
        <TabsList>
          <TabsTrigger value="announcements" className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Announcements
          </TabsTrigger>
          <TabsTrigger value="direct" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Direct Messages
            {conversations.filter(c => {
              const isInitiator = c.initiated_by === user.id;
              return isInitiator ? c.unread_count_sender > 0 : c.unread_count_recipient > 0;
            }).length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {conversations.filter(c => {
                  const isInitiator = c.initiated_by === user.id;
                  return isInitiator ? c.unread_count_sender > 0 : c.unread_count_recipient > 0;
                }).length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Announcements Tab */}
        <TabsContent value="announcements" className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading announcements...</div>
          ) : announcements.length === 0 ? (
            <div className="card p-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No announcements at this time</p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((ann) => (
                <div key={ann.id} className={`card p-4 border-l-4 ${
                  ann.priority === 'urgent' ? 'border-l-red-500' :
                  ann.priority === 'important' ? 'border-l-amber-500' :
                  'border-l-emerald-500'
                }`}>
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-slate-900">{ann.title}</h3>
                    {getPriorityBadge(ann.priority)}
                  </div>
                  <p className="text-slate-600 whitespace-pre-wrap">{ann.content}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                    <span>By {ann.created_by_name}</span>
                    <span>{formatDate(ann.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Direct Messages Tab */}
        <TabsContent value="direct" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Your Conversations</h3>
            <Dialog open={newMessageOpen} onOpenChange={setNewMessageOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-message-btn" className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" /> New Message
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send New Message</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSendNewMessage} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Send To</label>
                    <div className="mt-2 space-y-2">
                      {contacts.length === 0 ? (
                        <p className="text-sm text-slate-500">No contacts available</p>
                      ) : (
                        contacts.map((contact) => (
                          <label
                            key={contact.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              newMessageData.recipient_id === contact.id 
                                ? 'border-emerald-500 bg-emerald-50' 
                                : 'border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="recipient"
                              value={contact.id}
                              checked={newMessageData.recipient_id === contact.id}
                              onChange={() => setNewMessageData({...newMessageData, recipient_id: contact.id})}
                              className="text-emerald-600"
                            />
                            <div className="flex items-center gap-2">
                              {contact.role === 'admin' ? (
                                <Shield className="w-4 h-4 text-red-500" />
                              ) : (
                                <User className="w-4 h-4 text-purple-500" />
                              )}
                              <span className="font-medium">{contact.name}</span>
                              {getRoleBadge(contact.role)}
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Subject</label>
                    <Input
                      value={newMessageData.subject}
                      onChange={(e) => setNewMessageData({...newMessageData, subject: e.target.value})}
                      placeholder="Message subject..."
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Message</label>
                    <Textarea
                      value={newMessageData.content}
                      onChange={(e) => setNewMessageData({...newMessageData, content: e.target.value})}
                      placeholder="Write your message..."
                      rows={4}
                      className="mt-1"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Send className="w-4 h-4 mr-2" /> Send Message
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading conversations...</div>
          ) : conversations.length === 0 ? (
            <div className="card p-8 text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">No conversations yet</p>
              <p className="text-sm text-slate-400">Click "New Message" to start a conversation with the farm</p>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => {
                const isInitiator = conv.initiated_by === user.id;
                const otherPartyName = isInitiator ? conv.recipient_name : conv.initiated_by_name;
                const otherPartyRole = isInitiator ? conv.recipient_role : conv.initiated_by_role;
                const unreadCount = isInitiator ? conv.unread_count_sender : conv.unread_count_recipient;
                
                return (
                  <div
                    key={conv.id}
                    data-testid={`conversation-${conv.id}`}
                    className={`card p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      unreadCount > 0 ? 'border-l-4 border-l-emerald-500' : ''
                    }`}
                    onClick={() => openConversation(conv)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-900">{conv.subject}</span>
                          {unreadCount > 0 && (
                            <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">
                              {unreadCount} new
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <span>with {otherPartyName}</span>
                          {getRoleBadge(otherPartyRole)}
                        </div>
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                          {conv.last_message_preview}
                        </p>
                      </div>
                      <span className="text-xs text-slate-400">
                        {formatDate(conv.last_message_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomerMessages;
